import { useRef } from 'react'
import { NetworkStatus } from '@apollo/client'
import { StoredFilterParams } from '@equipmentshare/dcf-search-filter-experience'
import { useItemMastersQuery as useItemMastersQueryGenerated, ItemMastersQuery } from '../../../../graphql'

export type ItemMasterRow = NonNullable<ItemMastersQuery['itemMasters']['edges'][number]['node']>

export type ItemMastersSearchRequest = {
	search?: string,
	page?: number,
	size?: number,
	isActive?: string,
}

export default function useItemMastersQuery(
	apiFilterParams: StoredFilterParams<ItemMastersSearchRequest>
) {
	const size = apiFilterParams.size
		? Number( Array.isArray( apiFilterParams.size ) ? apiFilterParams.size[ 0 ] : apiFilterParams.size )
		: 25
	const page = apiFilterParams.page
		? Number( Array.isArray( apiFilterParams.page ) ? apiFilterParams.page[ 0 ] : apiFilterParams.page )
		: 0

	const searchRaw = Array.isArray( apiFilterParams.search )
		? apiFilterParams.search[ 0 ]
		: apiFilterParams.search
	const search = searchRaw && searchRaw.trim() ? searchRaw.trim() : undefined

	const isActiveRaw = Array.isArray( apiFilterParams.isActive )
		? apiFilterParams.isActive[ 0 ]
		: apiFilterParams.isActive
	const isActive = isActiveRaw === 'true' ? true : isActiveRaw === 'false' ? false : undefined

	// Track end cursors indexed by page number so forward pagination works.
	// Reset the map whenever filter or size changes.
	const cursorsRef = useRef<( string | undefined )[]>( [ undefined ] )
	const prevSizeRef = useRef( size )
	const prevIsActiveRef = useRef( isActiveRaw )
	const prevSearchRef = useRef( search )

	if (
		prevSizeRef.current !== size ||
		prevIsActiveRef.current !== isActiveRaw ||
		prevSearchRef.current !== search
	) {
		cursorsRef.current = [ undefined ]
		prevSizeRef.current = size
		prevIsActiveRef.current = isActiveRaw
		prevSearchRef.current = search
	}

	const { data, previousData, networkStatus } = useItemMastersQueryGenerated( {
		variables: {
			isActive,
			search,
			paging: {
				first: size,
				after: cursorsRef.current[ page ],
			},
		},
		notifyOnNetworkStatusChange: true,
	} )

	const displayData = data ?? previousData

	if ( displayData?.itemMasters.pageInfo.endCursor ) {
		cursorsRef.current[ page + 1 ] = displayData.itemMasters.pageInfo.endCursor
	}

	const rows = displayData?.itemMasters.edges.map( ( edge ) => edge.node ) ?? []

	return {
		isFetching: networkStatus !== NetworkStatus.ready,
		data: displayData?.itemMasters
			? {
				rows,
				rowCount: displayData.itemMasters.totalCount,
			}
			: undefined,
	}
}
